// The client's replica of canonical state, and the logic that decides when to
// repair.
//
// A v3 client is a follower of the room, full stop. It holds a replica, applies
// accepted ops in room order, and when it notices its Live disagrees with the
// replica it PULLS a repair. There is no path by which it pushes its own view at
// anyone — which is the difference between this and the leader it replaces.
//
// Contiguity: `roomRev` increments by exactly one per accepted envelope, so a
// replica can tell "I have everything up to here" from a single integer. A gap
// means ops were missed (log truncation, a long disconnect) and the honest
// response is to re-read the whole truth rather than guess at the middle.
//
// Pure: no SDK, no I/O, no `node:` imports. The SDK adapter drives Live from the
// ops this module hands it.

import { CENTER_PAN, UNITY_VOLUME } from "../mixer.js";
import { occurrenceKeys, type CanonicalSet } from "./canonical.js";
import type { OpBroadcast, Rejection, RoomStateResponse } from "./intents.js";
import {
  applyAcceptedOps,
  cloneDevices,
  emptyRoom,
  toCanonicalSet,
  type AcceptedOp,
  type CanonicalRoom,
} from "./state.js";

export interface BroadcastOutcome {
  /** Ops the caller should drive into Live, in order. Empty on a replay. */
  apply: AcceptedOp[];
  /** A gap was detected — re-read /state before trusting anything further. */
  needsFullState: boolean;
  /** Already applied; nothing to do (a proposer seeing its own echo). */
  duplicate: boolean;
}

export class Replica {
  room: CanonicalRoom = emptyRoom();
  /** Highest roomRev fully applied to Live. */
  appliedRoomRev = 0;
  /** The life of the room these revs belong to, as the last full state named
   * it; null until one does (or from an old server that sends none). */
  epoch: string | null = null;

  /**
   * Fold a broadcast in. Idempotent on replay, and refuses to apply across a
   * gap — applying ops out of order is how a replica ends up in a state neither
   * side ever held.
   */
  applyBroadcast(broadcast: OpBroadcast): BroadcastOutcome {
    // A frame from another life of the room (a reset): its revs restart from
    // zero, so by roomRev alone it reads as a replay — and so does every frame
    // after it, until the new life outgrows the old one (2026-10-29, the jam
    // fuzzer: a web tab sat on the reset room's old content).
    if (broadcast.epoch !== undefined && this.epoch !== null && broadcast.epoch !== this.epoch) {
      return { apply: [], needsFullState: true, duplicate: false };
    }
    if (broadcast.roomRev <= this.appliedRoomRev) {
      return { apply: [], needsFullState: false, duplicate: true };
    }
    if (broadcast.roomRev !== this.appliedRoomRev + 1) {
      return { apply: [], needsFullState: true, duplicate: false };
    }
    applyAcceptedOps(this.room, broadcast.ops);
    this.room.roomRev = broadcast.roomRev;
    this.appliedRoomRev = broadcast.roomRev;
    return { apply: broadcast.ops, needsFullState: false, duplicate: false };
  }

  /** Adopt the room's whole truth (late join, gap, or post-rejection resync). */
  adoptFullState(state: RoomStateResponse): void {
    this.epoch = state.epoch ?? null;
    this.room = {
      roomRev: state.roomRev,
      tempo: state.tempo,
      tempoRev: state.tempoRev,
      // Pre-volume/pan servers omit the mixer fields — default to unity and
      // center so the replica never manufactures a fader move out of a
      // missing field.
      tracks: state.tracks.map((t) => ({
        ...t,
        volume: t.volume ?? UNITY_VOLUME,
        pan: t.pan ?? CENTER_PAN,
      })),
      clips: state.clips.map((c) => ({ ...c })),
      sceneNames: state.sceneNames ?? [],
      scenesRev: state.scenesRev ?? 0,
      cuePoints: (state.cuePoints ?? []).map((c) => ({ name: c.name, time: c.time })),
      cuePointsRev: state.cuePointsRev ?? 0,
      master: state.master ? { volume: state.master.volume, pan: state.master.pan } : { volume: UNITY_VOLUME, pan: CENTER_PAN },
      masterRev: state.masterRev ?? 0,
      returns: (state.returns ?? []).map((r) => ({ ...r })),
      returnsRev: state.returnsRev ?? 0,
      chains: Object.fromEntries(Object.entries(state.chains ?? {}).map(([owner, c]) => [owner, { devices: cloneDevices(c.devices), rev: c.rev }])),
      ...(state.scale ? { scale: { ...state.scale, intervals: [...state.scale.intervals] } } : {}),
      scaleRev: state.scaleRev ?? 0,
    };
    this.appliedRoomRev = state.roomRev;
  }

  /**
   * What to do about rejections. A `stale` rejection means the room moved on
   * without us, so the replica is behind and the local edit was compiled against
   * a view that no longer holds: re-read and let the next poll recompile. The
   * caller must NOT retry the intent as-is — that is how a stale write becomes a
   * loop.
   */
  onRejections(rejected: Rejection[]): { needsFullState: boolean; reasons: string[] } {
    const reasons = rejected.map((r) => `${r.intent.kind}: ${r.reason}`);
    const needsFullState = rejected.some(
      (r) => r.reason === "stale" || r.reason === "unknown-track" || r.reason === "unknown-clip",
    );
    return { needsFullState, reasons };
  }

  /** The replica projected onto the digest comparison surface. */
  canonicalSet(): CanonicalSet {
    return toCanonicalSet(this.room);
  }

  // planRepair / static laneDigests / reset were deleted in v4 step 7: repair
  // comparison moved to the ID-KEYED surface (shared/sync/lane-digests.ts,
  // driven by JamClient with its bindings), and nothing ever called reset()
  // — sessions construct a fresh Replica instead.
}

export { occurrenceKeys };
